// MarketingNav.jsx
//
// Sticky top navigation shown on every unauthenticated marketing page.
// Layout:
//
//   [logo + DocketFlow]   Features  Pricing  About            [Sign in] [Book a demo]
//
// Mobile: collapses to a hamburger that opens a slide-down panel. The
// "Book a demo" CTA stays visible at all sizes — it's the primary
// conversion action.

import React, { useEffect, useState } from 'react';
import { Menu, X, Play } from 'lucide-react';
import { BRAND, ROUTES, goTo, scrollToSection } from './marketingTokens';

// Dispatches the same event WatchDemoModal listens for. Hoisted out of
// component so it works whether the nav is in the marketing surface or
// (in the future) anywhere else.
const openDemo = () => window.dispatchEvent(new CustomEvent('docketflow:openDemo'));

export default function MarketingNav({ activeRoute = 'home' }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Add a subtle shadow once the user scrolls past the hero — this is
  // the same affordance Linear / Vercel / Notion use to indicate the
  // nav is pinned, without dropping a hard divider line on the hero.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile menu whenever the user navigates anywhere.
  useEffect(() => {
    const onHashChange = () => setOpen(false);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const navItem = (label, target, key) => {
    const isActive = activeRoute === key;
    return (
      <button
        key={label}
        onClick={() => {
          // For anchor targets, scroll on the home page; otherwise navigate.
          if (key === 'features' || key === 'pricing' || key === 'contact') {
            if (window.location.hash === '#/' || window.location.hash === '') {
              scrollToSection(key);
            } else {
              goTo(key);
            }
          } else {
            goTo(key);
          }
          setOpen(false);
        }}
        className="text-sm font-medium transition-colors px-3 py-2 rounded-lg"
        style={{
          color:   isActive ? BRAND.blueDeep : BRAND.textMuted,
          background: isActive ? BRAND.blueGhost : 'transparent',
        }}
        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = BRAND.blueDeep; }}
        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = BRAND.textMuted; }}
      >
        {label}
      </button>
    );
  };

  return (
    <header
      className="sticky top-0 z-50 w-full transition-shadow"
      style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'saturate(180%) blur(12px)',
        WebkitBackdropFilter: 'saturate(180%) blur(12px)',
        boxShadow: scrolled ? '0 1px 0 rgba(15,23,42,0.06), 0 6px 24px rgba(15,23,42,0.04)' : 'none',
        borderBottom: scrolled ? `1px solid ${BRAND.border}` : '1px solid transparent',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* ── Logo (clickable, returns to home) ───────────────────── */}
          <button
            onClick={() => goTo('home')}
            className="flex items-center gap-2.5 group"
          >
            <img
              src="/logo-transparent.png"
              alt="DocketFlow"
              className="w-9 h-9 object-contain transition-transform group-hover:scale-105"
            />
            <span
              className="text-lg font-bold tracking-tight hidden sm:inline"
              style={{ color: BRAND.blueDeep }}
            >
              DocketFlow
            </span>
          </button>

          {/* ── Desktop nav links ───────────────────────────────────── */}
          <nav className="hidden md:flex items-center gap-1">
            {navItem('Features', ROUTES.features, 'features')}
            {navItem('Pricing',  ROUTES.pricing,  'pricing')}
            {navItem('Security', ROUTES.security, 'security')}
            {navItem('About',    ROUTES.about,    'about')}
          </nav>

          {/* ── Right side actions ──────────────────────────────────── */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* "Watch demo" — opens the in-page modal with the animated
                demo. Zero-friction preview of the product, available
                from every page. */}
            <button
              onClick={openDemo}
              className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium transition-colors px-3 py-2 rounded-lg"
              style={{ color: BRAND.blueDeep }}
              onMouseEnter={(e) => { e.currentTarget.style.background = BRAND.blueGhost; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Play size={11} fill="currentColor" />
              Watch demo
            </button>
            {/* "Sign in" is intentionally a small text link — the page's
                primary action is "Book a demo" / "Start free trial", not
                signing in. Signing in is for return visitors. */}
            <button
              onClick={() => goTo('login')}
              className="hidden sm:inline text-sm font-medium transition-colors px-3 py-2 rounded-lg"
              style={{ color: BRAND.textMuted }}
              onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.blueDeep; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
            >
              Sign in
            </button>
            <button
              onClick={() => goTo('bookDemo')}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg transition-all"
              style={{
                background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
                color: '#fff',
                boxShadow: '0 4px 16px rgba(249,115,22,0.32)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(249,115,22,0.45)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(249,115,22,0.32)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Book a demo
            </button>

            {/* ── Mobile menu toggle ─────────────────────────────── */}
            <button
              onClick={() => setOpen((o) => !o)}
              className="md:hidden p-2 rounded-lg transition-colors"
              style={{ color: BRAND.blueDeep }}
              aria-label={open ? 'Close menu' : 'Open menu'}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile slide-down panel ─────────────────────────────── */}
      {open && (
        <div
          className="md:hidden border-t"
          style={{ background: BRAND.bgWhite, borderColor: BRAND.border }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 space-y-1">
            {navItem('Features', ROUTES.features, 'features')}
            {navItem('Pricing',  ROUTES.pricing,  'pricing')}
            {navItem('Security', ROUTES.security, 'security')}
            {navItem('About',    ROUTES.about,    'about')}
            <div className="pt-2 mt-2 border-t flex flex-col gap-2" style={{ borderColor: BRAND.border }}>
              <button
                onClick={() => { openDemo(); setOpen(false); }}
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg text-left"
                style={{ color: BRAND.blueDeep, background: BRAND.blueGhost }}
              >
                <Play size={11} fill="currentColor" />
                Watch demo (30s)
              </button>
              <button
                onClick={() => { goTo('login'); setOpen(false); }}
                className="text-sm font-medium px-3 py-2 rounded-lg text-left"
                style={{ color: BRAND.blueDeep, background: BRAND.bgLight }}
              >
                Sign in
              </button>
              <button
                onClick={() => { goTo('bookDemo'); setOpen(false); }}
                className="text-sm font-semibold px-3 py-2.5 rounded-lg text-center"
                style={{
                  background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
                  color: '#fff',
                }}
              >
                Book a demo
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
